import { t } from "i18next";
import moment from "moment";

import { allCtaActions } from "../components/deliveryCtaSection/DeliveryCtaSection";
import { InboundDeliveryAction } from "../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../models/deliveryItem";

const isNullOrEmpty = (str: any): boolean => {
  if (
    typeof str === "undefined" ||
    str == null ||
    str.toString().length === 0 ||
    str.toString().trim().length === 0
  ) {
    return true;
  } else {
    return false;
  }
};

const copyAndSort = <T>(
  items: T[],
  columnKey: string,
  isSortedDescending?: boolean,
): T[] => {
  const key = columnKey as keyof T;
  return items
    .slice(0)
    .sort((a: T, b: T) =>
      (isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1,
    );
};

//checks if a given string has character length between min - max
//an empty field will not return an error
const charsWithinScopeValidation = (
  value: string,
  min: number,
  max: number,
) => {
  let errorText: string = "";
  if (value != null && value.length < min && value.length > 0) {
    return (errorText = t("tooFewChars", { min }));
  }
  if (value != null && value.length > max) {
    return (errorText = t("tooManyChars", { max }));
  }
  return errorText;
};

//checks if string includes @ symbol
//also calls charsWithinScopeValidation
//an empty field will not return an error
const emailValidation = (value: string, min: number, max: number) => {
  let errorText: string = "";
  const withinScopeError: string = charsWithinScopeValidation(value, min, max);
  const isWithinScope: boolean = isNullOrEmpty(withinScopeError);
  if (!isWithinScope) {
    return (errorText = withinScopeError);
  }
  if (value != null && value.length !== 0 && value.indexOf("@") === -1) {
    return (errorText = t("needs@Symbol"));
  }
  return errorText;
};

//checks if a given string matches the regExp "^\\d{5}$"
//also calls charsWithinScopeValidation
//an empty field will not return an error
const postalCodeValidation = (value: string, min: number, max: number) => {
  let errorText: string = "";
  const validationRule = "^\\d{5}$";
  const withinScopeError: string = charsWithinScopeValidation(value, min, max);
  const isWithinScope: boolean = isNullOrEmpty(withinScopeError);
  if (!isWithinScope) {
    return (errorText = withinScopeError);
  }
  if (value != null && value.length !== 0 && !value.match(validationRule)) {
    return (errorText = "Wrong postal Code format");
  }
  return errorText;
};

const transferAmountValidation = (value: number, maxAmount: number) => {
  let errorText: string = "";
  if (value > maxAmount) {
    return (errorText = t("valueTooHigh", { maxAmount }));
  }
  if (value <= 0) {
    return (errorText = t("valueTooLow"));
  }
  return errorText;
};

//rounds up to the next quarter hour from given minutes
const nextQuarterHour = (minutes: number) => {
  if (minutes % 15 === 0) {
    minutes++;
  }
  return (Math.ceil(minutes / 15) * 15) % 60;
};

//sets the minutes of a Date object and defaulting secs and ms to 0
const setDateTimeToMinutes = (dateTime: Date, minutes: number) => {
  dateTime.setMinutes(minutes);
  dateTime.setSeconds(0);
  dateTime.setMilliseconds(0);
  return dateTime;
};

//adding 0 in front of the number if below 10
const addZeroInFront = (time: number) => {
  return time < 10 ? `0${time}` : time;
};

//Compares two objects and returns true if they have the same properties and values.
//If an object contains arrays or nested objects, this function will recursively compare their contents.
const objectsHaveEqualValues = <T extends object>(obj1: T, obj2: T) => {
  //Get the keys of both objects and compare their lengths
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);

  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }
  //Iterate over the keys of the first object
  for (const key of obj1Keys) {
    //Get the value of the key from both objects
    const val1 = obj1[key as keyof T] as object;
    const val2 = obj2[key as keyof T] as object;

    // If the values are objects, recursively call objectsHaveEqualValues
    if (typeof val1 === "object" && typeof val2 === "object") {
      if (!objectsHaveEqualValues(val1, val2)) {
        return false;
      }
    }
    //If both values are arrays, iterate over each element of the arrays and recursively call objectsHaveEqualValues
    else if (Array.isArray(val1) && Array.isArray(val2)) {
      if (val1.length !== val2.length) {
        return false;
      }
      for (let i = 0; i < val1.length; i++) {
        //if an array is nested inside an array, recursively compare each element
        if (Array.isArray(val1[i]) && Array.isArray(val2[i])) {
          if (!objectsHaveEqualValues(val1[i], val2[i])) {
            return false;
          }
          //if an object is nested inside the array, recursively each element
        } else if (typeof val1[i] === "object" && typeof val2[i] === "object") {
          if (!objectsHaveEqualValues(val1[i], val2[i])) {
            return false;
          }
        } else if (val1[i] !== val2[i]) {
          return false;
        }
      }
    }
    // If the values are not objects or arrays, compare them directly
    else if (val1 !== val2) {
      return false;
    }
  }
  //If all steps have been successfully completed, the two objects are equal
  return true;
};

//Builds a URL string from a base URL and query parameters.
const buildUrlWithParams = (baseUrl: string, params: Record<string, any>) => {
  let url = baseUrl + "?";
  //Loop over each key in the params object
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null) {
      //Check if the value of the current key is an array
      if (Array.isArray(value)) {
        //Loop over each value in the array and add it to the URL as a separate query parameter with the same key
        value.forEach((item) => {
          url += `${key}=${item}&`;
        });
      } else {
        //If the value is not an array, add it to the URL as a query parameter with the current key
        url += `${key}=${value}&`;
      }
    }
  });
  return url.slice(0, -1); // remove last "&" character
};

//Merges two objects, with properties in the second object taking precedence.
//If both objects have a property with an array value, the arrays are merged.
const mergeObjects = (obj1: Record<string, any>, obj2: Record<string, any>) => {
  //Merge the properties from the first object
  const merged = { ...obj1 };
  //Merge the properties from the second object, overwriting any matching properties from the first object
  Object.keys(obj2).forEach((key) => {
    //If both objects have a property with an array as a value, merge the arrays
    if (Array.isArray(obj2[key]) && Array.isArray(merged[key])) {
      merged[key] = [...merged[key], ...obj2[key]];
    } else {
      merged[key] = obj2[key];
    }
  });

  return merged;
};

const isToday = (isoDateString: string): boolean => {
  const today = new Date().toISOString().slice(0, 10);
  return isoDateString.slice(0, 10) === today;
};

const parseEnumFromString = <T extends {}>(
  enumObj: T,
  str?: string,
): T[keyof T] | undefined => {
  if (!str) {
    return undefined;
  }
  const key = Object.keys(enumObj).find((k) => enumObj[k as keyof T] === str);
  return key ? enumObj[key as keyof T] : undefined;
};

const getLowestAvailablePosition = (positions: number[]): number => {
  if (positions.length === 0) return 10;

  positions.sort((a, b) => a - b);

  let position = 10;
  for (let i = 0; i < positions.length; i++) {
    if (positions[i] !== position) {
      return position;
    }
    position += 10;
  }

  return position;
};

const isValidString = (value: any): value is string => {
  return typeof value === "string" && value.trim() !== "";
};

const fileSizeInMb = (file: File): string =>
  (file.size / 1024 / 1000).toFixed(2) + " MB";

//we filter out the position of the item that we are editing, because it would trigger a position validation error
const calcExistingDeliveryItemPositions = (
  deliveryItems: IDeliveryItem[],
  itemToEdit: IDeliveryItem | null,
) =>
  deliveryItems
    .filter((item) =>
      itemToEdit ? item.position !== itemToEdit.position : true,
    )
    .map((item) => Number(item.position));

const isValidDeliveryDate = (plannedDelivery: Date | null) => {
  return (
    plannedDelivery instanceof Date &&
    !isNaN(plannedDelivery.getTime()) &&
    plannedDelivery > new Date() &&
    plannedDelivery < moment().add(6, "months").toDate()
  );
};

const sortedDeliveryActions = (actions: InboundDeliveryAction[]) => {
  const otherActions = actions.filter(
    (action) => !allCtaActions.includes(action),
  );

  //custom sorting, ctaActions first, then otherActions
  return [...actions].sort((a, b) => {
    const aIndex =
      allCtaActions.indexOf(a) !== -1
        ? allCtaActions.indexOf(a)
        : otherActions.indexOf(a) + allCtaActions.length;
    const bIndex =
      allCtaActions.indexOf(b) !== -1
        ? allCtaActions.indexOf(b)
        : otherActions.indexOf(b) + allCtaActions.length;
    return aIndex - bIndex;
  });
};

const formattedTimeSlotString = (start: string, end: string) => {
  return `${moment(new Date(start))
    .locale(t("locale"))
    .format(t("untilTime"))} - ${moment(new Date(end))
    .locale(t("locale"))
    .format(t("untilTimeFull"))}`;
};

export {
  addZeroInFront,
  buildUrlWithParams,
  calcExistingDeliveryItemPositions,
  charsWithinScopeValidation,
  copyAndSort,
  emailValidation,
  fileSizeInMb,
  formattedTimeSlotString,
  getLowestAvailablePosition,
  isNullOrEmpty,
  isToday,
  isValidDeliveryDate,
  isValidString,
  mergeObjects,
  nextQuarterHour,
  objectsHaveEqualValues,
  parseEnumFromString,
  postalCodeValidation,
  setDateTimeToMinutes,
  sortedDeliveryActions,
  transferAmountValidation,
};
